import type { User } from '@/types'
import { getApiUrl } from './apiUrl'

// Security configuration
export const SECURITY_CONFIG = {
  // Session timeout (in minutes)
  SESSION_TIMEOUT: 60,
  
  // Rate limiting
  RATE_LIMITS: {
    LOGIN_ATTEMPTS: 5,
    LOGIN_WINDOW: 15, // minutes
    API_CALLS: 100,
    API_WINDOW: 60 // minutes
  },
  
  // Password requirements
  PASSWORD: {
    MIN_LENGTH: 8,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBERS: true,
    REQUIRE_SYMBOLS: true
  }
}

// Security service class
export class SecurityService {
  private static instance: SecurityService
  private loginAttempts: Map<string, { count: number; lastAttempt: Date }> = new Map()
  private apiCalls: Map<string, { count: number; windowStart: Date }> = new Map()

  static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService()
    }
    return SecurityService.instance
  }

  /**
   * Check if user has exceeded login attempts
   */
  async checkLoginAttempts(email: string): Promise<{ allowed: boolean; remainingAttempts: number }> {
    const attempts = this.loginAttempts.get(email)
    const now = new Date()
    
    if (!attempts) {
      return { allowed: true, remainingAttempts: SECURITY_CONFIG.RATE_LIMITS.LOGIN_ATTEMPTS }
    }

    // Check if window has expired
    const windowExpiry = new Date(attempts.lastAttempt.getTime() + SECURITY_CONFIG.RATE_LIMITS.LOGIN_WINDOW * 60 * 1000)
    if (now > windowExpiry) {
      this.loginAttempts.delete(email)
      return { allowed: true, remainingAttempts: SECURITY_CONFIG.RATE_LIMITS.LOGIN_ATTEMPTS }
    }

    const remaining = Math.max(0, SECURITY_CONFIG.RATE_LIMITS.LOGIN_ATTEMPTS - attempts.count)
    return { allowed: attempts.count < SECURITY_CONFIG.RATE_LIMITS.LOGIN_ATTEMPTS, remainingAttempts: remaining }
  }

  /**
   * Record a failed login attempt
   */
  async recordFailedLogin(email: string): Promise<void> {
    const attempts = this.loginAttempts.get(email)
    const now = new Date()
    
    if (attempts) {
      attempts.count++
      attempts.lastAttempt = now
    } else {
      this.loginAttempts.set(email, { count: 1, lastAttempt: now })
    }
  }

  /**
   * Clear login attempts for successful login
   */
  async clearLoginAttempts(email: string): Promise<void> {
    this.loginAttempts.delete(email)
  }

  /**
   * Check API rate limits
   */
  async checkApiRateLimit(userId: string): Promise<{ allowed: boolean; remainingCalls: number }> {
    const calls = this.apiCalls.get(userId)
    const now = new Date()
    
    if (!calls) {
      this.apiCalls.set(userId, { count: 1, windowStart: now })
      return { allowed: true, remainingCalls: SECURITY_CONFIG.RATE_LIMITS.API_CALLS - 1 }
    }

    // Check if window has expired
    const windowExpiry = new Date(calls.windowStart.getTime() + SECURITY_CONFIG.RATE_LIMITS.API_WINDOW * 60 * 1000)
    if (now > windowExpiry) {
      this.apiCalls.set(userId, { count: 1, windowStart: now })
      return { allowed: true, remainingCalls: SECURITY_CONFIG.RATE_LIMITS.API_CALLS - 1 }
    }

    const remaining = Math.max(0, SECURITY_CONFIG.RATE_LIMITS.API_CALLS - calls.count)
    return { allowed: calls.count < SECURITY_CONFIG.RATE_LIMITS.API_CALLS, remainingCalls: remaining }
  }

  /**
   * Record API call
   */
  async recordApiCall(userId: string): Promise<void> {
    const calls = this.apiCalls.get(userId)
    const now = new Date()
    
    if (calls) {
      calls.count++
    } else {
      this.apiCalls.set(userId, { count: 1, windowStart: now })
    }
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): { valid: boolean; errors: string[] } {
    const errors: string[] = []
    
    if (password.length < SECURITY_CONFIG.PASSWORD.MIN_LENGTH) {
      errors.push(`Password must be at least ${SECURITY_CONFIG.PASSWORD.MIN_LENGTH} characters long`)
    }
    
    if (SECURITY_CONFIG.PASSWORD.REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter')
    }
    
    if (SECURITY_CONFIG.PASSWORD.REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter')
    }
    
    if (SECURITY_CONFIG.PASSWORD.REQUIRE_NUMBERS && !/\d/.test(password)) {
      errors.push('Password must contain at least one number')
    }
    
    if (SECURITY_CONFIG.PASSWORD.REQUIRE_SYMBOLS && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character')
    }
    
    return { valid: errors.length === 0, errors }
  }

  /**
   * Sanitize user input
   */
  sanitizeInput(input: string): string {
    return input
      .trim()
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .replace(/['"]/g, '') // Remove quotes
      .substring(0, 1000) // Limit length
  }

  /**
   * Generate secure token
   */
  generateSecureToken(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  /**
   * Check if user session is valid by verifying with the backend
   */
  async validateSession(_userId: string): Promise<{ valid: boolean; user?: User }> {
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        return { valid: false }
      }

      const apiUrl = getApiUrl()
      
      const response = await fetch(`${apiUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        return { valid: false }
      }

      const data = await response.json()
      return { 
        valid: true, 
        user: data.user 
      }
    } catch (error) {
      console.error('Session validation error:', error)
      return { valid: false }
    }
  }

  /**
   * Log security event to the backend for audit purposes
   */
  async logSecurityEvent(event: string, userId?: string, details?: any): Promise<void> {
    try {
      const token = localStorage.getItem('authToken')
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      
      // Log to console in development
      if (import.meta.env.DEV) {
        console.log('Security event:', { event, userId, details, timestamp: new Date().toISOString() })
      }

      // Send to backend if authenticated
      if (token) {
        await fetch(`${apiUrl}/auth/security-log`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            event,
            userId,
            details,
            timestamp: new Date().toISOString()
          })
        }).catch(() => {
          // Silently fail - security logging should not block user actions
        })
      }
    } catch (error) {
      // Silently fail - security logging should not block user actions
      console.error('Failed to log security event:', error)
    }
  }
}

// Export singleton instance
export const securityService = SecurityService.getInstance()
